// Level utility functions for session and character management

export interface LevelRange {
  min: number;
  max: number;
  isFlexible: boolean;
}

export interface LevelCheckResult {
  isEligible: boolean;
  warningType: "too-low" | "too-high" | null;
  warningMessage: string | null;
}

/**
 * Parse a level range string (e.g., "Level 1-6" or "Flexible Level 3-8")
 * into structured data
 */
export function parseLevelRange(levelRange: string): LevelRange {
  const match = levelRange.match(/(Flexible )?Level (\d+)-(\d+)/);

  if (match) {
    return {
      min: parseInt(match[2], 10),
      max: parseInt(match[3], 10),
      isFlexible: !!match[1],
    };
  }

  // Default fallback if parsing fails
  return {
    min: 1,
    max: 20,
    isFlexible: false,
  };
}

/**
 * Check if a character's level is appropriate for a session's level range
 * Returns eligibility status and warning information
 */
export function checkCharacterLevelEligibility(
  characterLevel: number,
  sessionLevelRange: string
): LevelCheckResult {
  const levelRange = parseLevelRange(sessionLevelRange);

  // Check if character level is within range
  if (characterLevel >= levelRange.min && characterLevel <= levelRange.max) {
    return {
      isEligible: true,
      warningType: null,
      warningMessage: null,
    };
  }

  // Character is outside the level range
  let warningType: "too-low" | "too-high";
  let warningMessage: string;

  if (characterLevel < levelRange.min) {
    warningType = "too-low";
    warningMessage = `This character (Level ${characterLevel}) is below the session requirement (${sessionLevelRange}). Please discuss with the session DM before joining.`;
  } else {
    warningType = "too-high";
    warningMessage = `This character (Level ${characterLevel}) is above the session requirement (${sessionLevelRange}). Please discuss with the session DM before joining.`;
  }

  return {
    isEligible: false,
    warningType,
    warningMessage,
  };
}
